import React, {
  ElementType,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  Ref,
  forwardRef,
  ComponentProps,
  useEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import TextField, {
  TextFieldProps,
} from '@mui/material/TextField';
import {
  InputBaseComponentProps,
} from '@mui/material/InputBase';
import {
  IMaskInput,
} from 'react-imask';
import {
  InputMask,
} from 'imask';
import {
  isEmptyString,
} from '@populateui/utils';

export type CursorPositionOnFocus = 'start' | 'end' | 'clickedPosition' | 'selectAll';
type Position = {
  start: number;
  end: number;
};

type MaskedInputProps = Pick<ComponentProps<typeof IMaskInput>, 'mask' | 'unmask' | 'onAccept' | 'onComplete'>;

type MaskedInputElementProps = InputBaseComponentProps & MaskedInputProps & {
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
};

const MaskedInputComponent = forwardRef<
HTMLInputElement,
MaskedInputElementProps
>(
  (props, ref) => (
    <IMaskInput
      {...props}
      inputRef={ref}
    />
  ),
);

type Props = Omit<TextFieldProps, 'value' | 'defaultValue' | 'onChange' | 'slotProps'> & MaskedInputProps & {
  value?: TextFieldProps['value'] | null;
  defaultValue?: TextFieldProps['defaultValue'] | null;
  cursorPositionOnFocus?: CursorPositionOnFocus;
  maskedPosition?: Position;
  showErrorText?: boolean;
  slotProps?: TextFieldProps['slotProps'];
  inputRef?: Ref<HTMLInputElement>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

function computeSelection(
  input: HTMLInputElement,
  cursorPositionOnFocus: CursorPositionOnFocus,
  maskedPosition?: Position,
): Position {
  const {
    start: maskedStart = 0,
    end: maskedEnd = 0,
  } = maskedPosition ?? {};

  const position: Position = { start: 0, end: 0 };

  switch (cursorPositionOnFocus) {
    case 'start':
      position.start = 0;
      position.end = 0;
      break;
    case 'end':
      position.start = input.value.length;
      position.end = input.value.length;
      break;
    case 'clickedPosition':
      position.start = input.selectionStart ?? 0;
      position.end = input.selectionEnd ?? 0;
      break;
    case 'selectAll':
    default:
      position.start = maskedStart;
      position.end = input.value.length - maskedEnd;
  }

  return position;
}

export default function MaskedTextField({
  mask,
  unmask,
  onAccept,
  onComplete,
  className,
  value,
  onFocus,
  cursorPositionOnFocus = 'selectAll',
  maskedPosition,
  showErrorText,
  inputRef,
  inputProps,
  slotProps,
  ...props
}: Props) {
  const hasInitialValue = typeof value === 'string' || typeof value === 'number';
  const [isFilled, setIsFilled] = useState<boolean>(hasInitialValue);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  useEffect(() => {
    const hasValue = typeof value === 'string' || typeof value === 'number';
    setIsFilled(hasValue);
    if (!hasValue) {
      setIsCompleted(false);
    }
  }, [value]);

  const handleAccept = (acceptedValue: string, mask: InputMask<any>) => {
    const currentValue = typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : '';

    if (acceptedValue === currentValue) {
      return;
    }

    setIsFilled(!isEmptyString(acceptedValue));
    setIsCompleted(false);
    onAccept?.(acceptedValue, mask);
  };

  const handleComplete = (completedValue: string, mask: InputMask<any>) => {
    setIsFilled(!isEmptyString(completedValue));
    setIsCompleted(true);
    onComplete?.(completedValue, mask);
  };

  const doAfterHandlersAndBeforeNextPaint = (fn: () => void) => {
    if (typeof queueMicrotask === 'function') queueMicrotask(fn);
    else Promise.resolve().then(fn);
  };

  const selectAppropriately = (input: HTMLInputElement) => {
    if (typeof input.setSelectionRange !== 'function') return;

    const { start, end } = computeSelection(input, cursorPositionOnFocus, maskedPosition);
    input.setSelectionRange(
      Math.max(start, 0),
      Math.max(0, Math.min(end, input.value.length)),
    );
  };

  const isFocusByPointerEventChain = useRef(false);

  const handleBlur = () => { isFocusByPointerEventChain.current = false; };
  const handlePointerDown = () => { isFocusByPointerEventChain.current = true; };
  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    if (!isFocusByPointerEventChain.current) {
      doAfterHandlersAndBeforeNextPaint(() => selectAppropriately(target));
    }

    isFocusByPointerEventChain.current = false;
    onFocus?.(e);
  };
  const handleMouseDown = (e: MouseEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    if (e.detail >= 3) {
      e.preventDefault();
      selectAppropriately(target);
    }
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    if ((e.metaKey || e.ctrlKey) && (e.key.toUpperCase() === 'A')) {
      e.preventDefault();
      selectAppropriately(target);
    }
    if (e.key === 'Tab') isFocusByPointerEventChain.current = false;
  };

  const maskedInputProps: MaskedInputElementProps = {
    mask,
    unmask,
    value: value ?? '',
    onAccept: handleAccept,
    onComplete: handleComplete,
    onFocus: handleFocus,
    inputRef,
  };

  const isIncompleteMask = isFilled && !isCompleted;

  return (
    <TextField
      {...props}
      value={value ?? ''}
      className={clsx('populate-input', { 'is-incomplete-mask': isIncompleteMask }, className)}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          inputComponent: MaskedInputComponent as ElementType<InputBaseComponentProps>,
          inputProps: maskedInputProps,
          inputRef,
        },
        htmlInput: {
          ...slotProps?.htmlInput,
          ...inputProps,
          spellCheck: false,
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          onMouseDown: handleMouseDown,
          onPointerDown: handlePointerDown,
        },
      }}
    />
  );
}
